<template>
    <v-form ref="form" class="pb-4 pt-2" data-cy="description-location-form">
        <v-row dense>
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('product_tabs.location') }}
                </span>
            </v-col>
            <v-col cols="12" md="10">
                <v-row dense class="mb-4">
                    <v-col order="2" order-md="1" cols="12" md="7">
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="formData.latitude"
                                    type="number"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="latitude"
                                    hide-spin-buttons
                                    :label="`${$t('latitude')}*`"
                                    :rules="[requiredRule('latitude'), latitudeRangeRule]"
                                    :readonly="isHotelDescriptionMode || !authStore.isTO1"
                                    :disabled="isHotelDescriptionMode || !authStore.isTO1"
                                    @update:model-value="flickMap"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="formData.longitude"
                                    type="number"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="longitude"
                                    hide-spin-buttons
                                    :label="`${$t('longitude')}*`"
                                    :rules="[requiredRule('longitude'), longitudeRangeRule]"
                                    :readonly="isHotelDescriptionMode || !authStore.isTO1"
                                    :disabled="isHotelDescriptionMode || !authStore.isTO1"
                                    @update:model-value="flickMap"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col
                        v-if="formData.latitude && formData.longitude && isShowMap"
                        order="1"
                        order-md="2"
                        cols="12"
                        md="5"
                    >
                        <v-card>
                            <v-card-text class="px-0 py-0">
                                <map-location
                                    :latitude="Number(formData.latitude)"
                                    :longitude="Number(formData.longitude)"
                                    :is-show-controls="false"
                                    height="300px"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn v-if="!isHotelDescriptionMode && authStore.isTO1" data-cy="save" :disabled="isLoading" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn
                class="cancel-btn"
                data-cy="cancel"
                :disabled="isLoading"
                :to="{name: 'supplierProducts'}"
                variant="text"
            >
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import MapLocation from '~/components/info/MapLocation.vue'

    const isLoading = ref<boolean>(false)
    const formData = ref({
        latitude: null,
        longitude: null,
    })
    const isShowMap = ref<boolean>(false)
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
    const hotelDescriptionId: string = inject('hotelDescriptionId', null)

    const app = useNuxtApp()
    const {t} = useI18n()
    const authStore = useAuthStore()

    watch(useHotelInfo, async () => {
        await loadData()
    })

    onMounted(async () => {
        if (supplierStore.fullContract?.hotelContentId || isHotelDescriptionMode) {
            await loadData()
        }
        if (formData.value.longitude && formData.value.latitude) isShowMap.value = true
    })

    async function loadData(): Promise<void> {
        const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId
        try {
            isLoading.value = true
            formData.value = await app.$api.hotelDescriptionLocation.get(hotelId)
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const form = ref<any>(null)

    const latitudeRangeRule = (v: any): boolean | string => {
        if (v === null || v === undefined || v === '') return true
        const numValue = Number(v)
        return (numValue >= -90 && numValue <= 90) || `${t('latitude')} ${t('validation.valid')}`
    }

    const longitudeRangeRule = (v: any): boolean | string => {
        if (v === null || v === undefined || v === '') return true
        const numValue = Number(v)
        return (numValue >= -180 && numValue <= 180) || `${t('longitude')} ${t('validation.valid')}`
    }

    const save = async (): Promise<void> => {
        try {
            // Validate form before saving
            const {valid} = await form.value.validate()
            if (!valid) {
                app.$toast.error(t('error_message.something_went_wrong'))
                return
            }

            isLoading.value = true
            const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId
            await app.$api.hotelDescriptionLocation.put(hotelId, formData.value)

            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const flickMap = (): void => {
        isShowMap.value = false
        nextTick().then(() => {
            isShowMap.value = true
        })
    }
</script>
